/**
 * Centralised API error normaliser.
 *
 * Maps fetch / HTTP failures to calm, contextual user-facing messages
 * so panels never display raw "Failed to fetch" or "HTTP 403" text.
 *
 * Usage in a page/panel:
 *
 *   try {
 *     const res = await fetch(`${API}/api/foo`);
 *     if (!res.ok) throw await apiErrorFromResponse(res, { context: 'users' });
 *     setData(await res.json());
 *   } catch (e) {
 *     setError(messageFor(e, { context: 'users' }));
 *   }
 *
 * The contract is intentionally additive: callers can keep throwing
 * `new Error('HTTP 403')` and `messageFor` will still translate that
 * into the right contextual message.
 *
 * Contexts recognised today:
 *   - 'users'         (User Management page)
 *   - 'integrations'  (Settings → Integrations)
 *   - 'health'        (Provider Health)
 *   - 'plan_gated'    (any feature-gate-blocked surface)
 *   - default fallback for everything else
 *
 * Extending: pass a `messages` override object to override any specific
 * status code -> message mapping for a given panel.
 */

/** Normalised error shape. */
export class ApiError extends Error {
  constructor(status, body, context) {
    super(`api_error_${status}`);
    this.status = status;
    this.body = body;
    this.context = context;
  }
}

/** Build an ApiError from a fetch Response (always read-only on the body). */
export async function apiErrorFromResponse(res, opts = {}) {
  let body = null;
  try { body = await res.clone().json(); }
  catch { try { body = await res.clone().text(); } catch { body = null; } }
  return new ApiError(res.status, body, opts.context);
}

/**
 * Safely render a FastAPI-style `detail` payload as a human string.
 *
 * `detail` may be:
 *   - undefined / null  → returns ''
 *   - string            → returned as-is
 *   - object with `message` (e.g. `{error: 'target_tenant_required', message: '…'}`)
 *                       → returns the `message`
 *   - object with `error` only           → returns the `error` slug
 *   - object with `messages: string[]`   → joins with ' • '
 *   - array (FastAPI 422 validation: `[{loc, msg, type}, …]`) → joins each `msg`
 *   - any other object                   → returns '' (caller falls back)
 *
 * NEVER returns `[object Object]`.
 */
export function formatBackendDetail(d) {
  if (d === undefined || d === null) return '';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const parts = d
      .map((item) => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        if (typeof item.msg === 'string') return item.msg;
        if (typeof item.message === 'string') return item.message;
        return '';
      })
      .filter(Boolean);
    return parts.join(' • ');
  }
  if (typeof d === 'object') {
    if (typeof d.message === 'string') return d.message;
    if (Array.isArray(d.messages)) return d.messages.filter(Boolean).join(' • ');
    if (typeof d.error === 'string') return d.error;
    return '';
  }
  return String(d);
}

/** Recognise our common "permission" / "plan" / "usage" status fingerprints. */
function detail(err) {
  if (!err || !err.body) return '';
  if (typeof err.body === 'string') return err.body;
  const fromDetail = formatBackendDetail(err.body.detail);
  if (fromDetail) return fromDetail;
  if (typeof err.body.message === 'string') return err.body.message;
  if (typeof err.body.error === 'string') return err.body.error;
  return '';
}

/**
 * Produce a calm, contextual user-facing string for any error caught from
 * a fetch/REST call. Never returns raw "Failed to fetch" / "HTTP 403".
 */
export function messageFor(err, opts = {}) {
  const context = (opts.context || (err && err.context) || 'default').toLowerCase();
  const status = err && (err.status || err.code);
  const raw = (err && err.message) ? String(err.message) : '';
  const lower = raw.toLowerCase();
  const overrides = opts.messages || {};

  // ── Network failure (fetch threw before any response landed). ─────────
  // Both Chrome and Safari throw `TypeError: Failed to fetch` for offline,
  // CORS, DNS, and connection-reset failures. We bucket all of those.
  if (!status && (lower.includes('failed to fetch')
                  || lower.includes('networkerror')
                  || lower.includes('load failed'))) {
    return overrides.network
      || 'QUOD-Finder could not reach the service. Please refresh or try again shortly.';
  }

  // ── HTTP-status branches ─────────────────────────────────────────────
  if (status === 401) {
    return overrides[401] || 'Your session has expired. Please sign in again.';
  }

  if (status === 403) {
    const d = detail(err).toLowerCase();
    // Plan / feature gate — backend uses `feature_not_available` /
    // `plan_gate` keys.
    if (d.includes('plan') || d.includes('feature_not_available') || d.includes('feature gate')) {
      return overrides.plan_gated || 'This feature is not available on your current plan.';
    }
    if (context === 'users') {
      return overrides[403]
        || 'You do not have permission to view or manage users. Contact an Owner or Admin if you need access.';
    }
    if (context === 'integrations') {
      return overrides[403]
        || 'You’re viewing integrations in read-only mode. Only Owners and Admins can add, rotate or clear provider keys.';
    }
    if (context === 'health') {
      return overrides[403]
        || 'Provider Health is only available to Owners and Admins.';
    }
    return overrides[403] || 'You do not have permission to perform this action.';
  }

  if (status === 404) {
    if (context === 'users' && detail(err).toLowerCase().includes('tenant')) {
      return 'Select a tenant to view users.';
    }
    return overrides[404] || 'We could not find what you were looking for.';
  }

  if (status === 422) {
    return overrides[422] || (detail(err) || 'Some details need correcting before we can save.');
  }

  if (status === 429) {
    const d = detail(err);
    return overrides[429] || (d || 'You have reached a usage limit for now. It will reset shortly.');
  }

  if (status === 503) {
    return overrides[503] || 'QUOD-Finder is temporarily unavailable. Please try again shortly.';
  }

  if (status >= 500) {
    return overrides.server
      || 'Something went wrong while loading this section. Please try again. If it continues, contact support.';
  }

  // ── Last-resort fallbacks. NEVER expose `HTTP 4xx` raw. ─────────────
  if (raw.match(/^HTTP \d+$/i)) {
    return 'Something went wrong loading this section. Please try again.';
  }
  return raw || 'Something went wrong. Please try again.';
}
